import { MosaicTrafficLightsValue } from "../../traffic-lights/MosaicTrafficLightsValue";
import { AitRowData } from "./AitRowData";
import { AitRowGroupData } from "./AitRowGroupData";
import {
  AitHeaderGroupData,
  AitHeaderRowData,
  AitLocationBodyCell,
  AitLocationHeaderCell,
  AitTableData,
  AitTablePosition,
  AitTableState,
} from "./interface";

/**
 * Update the core traffic-light value on a row, row-group and table.
 *
 * Internal helper used to synchronise `coreTrafficLight` across a row,
 * its parent row-group and the table data when a change occurs.
 *
 * @template T - The row/cell payload type
 * @param coreTrafficLight - New `MosaicTrafficLightsValue` to set
 * @param row - The row to update
 * @param rowGroup - The parent row group or header group
 * @param tableData - The table data object which will have its `coreTrafficLight` updated
 */
const updateCoreTrafficLight = <T>(
  coreTrafficLight: MosaicTrafficLightsValue,
  row: AitHeaderRowData<T> | AitRowData<T>,
  rowGroup: AitHeaderGroupData<T> | AitRowGroupData<T>,
  tableData: AitTableData<T>,
) => {
  row.coreTrafficLight = coreTrafficLight;
  rowGroup.coreTrafficLight = coreTrafficLight;
  tableData.coreTrafficLight = coreTrafficLight;
};

/**
 * Set `coreTrafficLight` to `ChangedUnknown` for the row at `location`.
 *
 * When the location refers to a header cell, the corresponding header row,
 * header group and table are updated. When the location refers to a body
 * cell the body row, row-group and table are updated. This is intended to
 * flag that the row has been changed and requires re-evaluation.
 *
 * @template T - The row/cell payload type
 * @param state - The `AitTableState` containing the `originalTableData` to update
 * @param location - Location pointing to either a header cell or a body cell
 * @returns void (state is mutated in-place)
 */
export const trSetTrafficLightToChangedUnknown = <T>(
  state: AitTableState<T>,
  location: AitLocationHeaderCell | AitLocationBodyCell,
): void => {
  if (
    location.position === AitTablePosition.headerCell &&
    state.originalTableData &&
    state.originalTableData.headerData
  ) {
    const headerData = state.originalTableData.headerData;
    const row = headerData.rows[location.row];
    /* istanbul ignore else */
    if (row && row.coreTrafficLight !== "ChangedUnknown") {
      updateCoreTrafficLight("ChangedUnknown", row, headerData, state.originalTableData);
    }
  }

  if (location.position === AitTablePosition.bodyCell && state.originalTableData) {
    const bodyData = state.originalTableData.bodyData;
    const rowGroup = bodyData[location.rowGroup];
    /* istanbul ignore else */
    if (rowGroup) {
      const row = rowGroup.rows[location.row];
      /* istanbul ignore else */
      if (row && row.coreTrafficLight !== "ChangedUnknown") {
        updateCoreTrafficLight("ChangedUnknown", row, rowGroup, state.originalTableData);
      }
    }
  }
};
